import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { IKeycloakTeam } from 'pages/superAdmin/TeamAccess';
import Pagination from '@mui/material/Pagination';
import AppActionsAPI from 'api/AppActionsAPI';

interface IActiveTeamsProps {
  teams: any;
  archiveTeam: any;
  getGroups: any;
}

// AppActions.team_name is a point-in-time snapshot written on each action, not
// a live join to Keycloak -- a team renamed since its last action still shows
// under its old name here until its next action.
const formatLastAccessed = (
  team: IKeycloakTeam,
  lastAccessedByTeamName: Record<string, string>,
): string => {
  const raw = team.name ? lastAccessedByTeamName[team.name] : undefined;
  if (!raw) {
    return 'Never';
  }
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'Never';
};

const ActiveTeams: React.FC<IActiveTeamsProps> = (props: IActiveTeamsProps) => {
  const { teams, getGroups } = props;
  const [searchVal, setSearchVal] = useState('');
  const [activeTeams, setActiveTeams] = useState<IKeycloakTeam[]>([]);
  const active: IKeycloakTeam[] = [];
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [lastAccessedByTeamName, setLastAccessedByTeamName] = useState<
    Record<string, string>
  >({});
  const resultsPerPage = 15;

  useEffect(() => {
    AppActionsAPI.getLastAccessedByTeam().then(setLastAccessedByTeamName);
  }, []);

  const pageItems = useMemo(() => {
    const start = page * resultsPerPage;
    return activeTeams.slice(start, start + resultsPerPage);
  }, [activeTeams, page]);

  const handlePageChange = (e: any, new_page: number) => {
    const handledPage = new_page - 1;
    setPage(handledPage);
  };

  const filterTeams = async (teamsFromProps: IKeycloakTeam[]) => {
    await Promise.all(
      teamsFromProps.map(async (team: IKeycloakTeam) => {
        if (
          !team.realmRoles?.includes('archived') &&
          !team.realmRoles?.includes('service-account')
        ) {
          active.push(team);
        }
      }),
    );
    setActiveTeams(active);
  };

  useEffect(() => {
    filterTeams(teams);
  }, [teams]);

  useEffect(() => {
    const totalPages = Math.ceil(activeTeams.length / resultsPerPage);
    if (totalPages >= 200) {
      setPageCount(199);
    } else if (totalPages <= 199) {
      setPageCount(totalPages);
    }
  }, [activeTeams]);

  const handleChange = (e: any) => {
    setSearchVal(e.target.value);
    if (!e.target.value) {
      getGroups();
    }
  };

  const handleSubmit = (): void => {
    setPage(0);
    setActiveTeams((prevTeams: any) => {
      const lowerCaseQuery = searchVal.toLowerCase();
      // Sort teams, placing matches first based on full name accuracy
      const sortedTeams = [...prevTeams].sort((a: any, b: any): number => {
        const aMatches = [a?.name].some((field) =>
          // validate field is present, skips value otherwise
          field?.toLowerCase()?.includes(lowerCaseQuery),
        );
        const bMatches = [b?.name].some((field) =>
          // validate field is present, skips value otherwise
          field?.toLowerCase()?.includes(lowerCaseQuery),
        );

        if (aMatches && !bMatches) {
          return -1;
        } // a comes before b
        if (!aMatches && bMatches) {
          return 1;
        } // b comes before a
        return 0; // Keep original order if both match or both don't match
      });
      return sortedTeams;
    });
  };

  return (
    <>
      <div className='team-content-search'>
        <div className='screening-input-field-container'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
          >
            <path
              d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
              stroke='var(--card-text)'
              strokeWidth='1.66667'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <input
            placeholder='Search teams'
            id='primary'
            type='text'
            className='screening-input-field-content'
            name='primary'
            value={searchVal}
            onChange={(e) => handleChange(e)}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </div>
      </div>
      <div className='admin-team-list'>
        <div className='admin-team-row admin-team-row-header'>
          <span className='admin-team-col'>Team</span>
          <span className='admin-team-col'>Team Admin</span>
          <span className='admin-team-col'>Last Accessed</span>
        </div>
        {pageItems.map((team: IKeycloakTeam, index: number) => {
          return (
            <Link to={`/_tv/4f7d2b4nj/${team.id}`} key={index}>
              <div className='admin-team-row'>
                <span className='admin-team-col'>{team.name}</span>
                <span className='admin-team-col'>
                  {team.attributes?.team_admin?.join(', ') || 'None'}
                </span>
                <span className='admin-team-col'>
                  {formatLastAccessed(team, lastAccessedByTeamName)}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
      <div className='screening-results-pagination-text'>
        Page {page + 1} of {pageCount}
      </div>
      <div className='screening-results-footer-action-group'>
        <div className='hb-pagination'>
          <Pagination
            onChange={handlePageChange}
            size='large'
            count={pageCount}
            shape='rounded'
            variant='text'
            page={page + 1}
          />
        </div>
      </div>
    </>
  );
};

export default ActiveTeams;
